// 데이터 분석 정의 — 저장 형식 · 판 비교 · 컬럼 설정 맞추기(docs/features/30-data-analysis.md).
//
// 저장 형식 규칙(docs/schema-compat.md): 불러온 정의의 **모르는 칸은 버리지 않는다**(화면이 고친 칸만 덮어 같은 객체로 다시 저장한다).
// 판(version)은 숫자 튜플로 견주고, 더 새 판이면 불러와 보여 주되 덮어쓰지 않는다(canWrite) — 판을 끌어내리지 않는다.
import { inferDataType, uniqueColumnNames } from './analysisQueryBuilder';

export const SPEC_VERSION = [1, 0];

/** 숫자 튜플 비교 — a < b 면 음수 */
export function compareVersion(a, b) {
  const x = Array.isArray(a) ? a : [];
  const y = Array.isArray(b) ? b : [];
  for (let i = 0; i < Math.max(x.length, y.length); i++) {
    const d = (Number(x[i]) || 0) - (Number(y[i]) || 0);
    if (d) return d;
  }
  return 0;
}

/** 이 빌드가 이 정의를 덮어써도 되는가 — 더 새 판은 읽기만 */
export const canWrite = (spec) => compareVersion(spec && spec.version, SPEC_VERSION) <= 0;

export const defaultTemplateParams = () => ({
  unique: { cols: [] },
  topn: { cols: [], n: 10, by: 'count', dir: 'DESC' },
  dup: { cols: [] },
  firstLast: { keys: [], orderCol: '', which: 'last' },
});

export const defaultPivot = () => ({
  mode: 'none', aggFn: 'SUM', pivotCol: '', valueCol: '', values: '',
  unpivotCols: [], nameAlias: 'COL_NAME', valueAlias: 'COL_VALUE', includeNulls: false,
});

export function defaultSpec() {
  return {
    version: SPEC_VERSION,
    source: { kind: 'table', schema: '', table: '' },
    template: 'custom',
    templateParams: defaultTemplateParams(),
    columns: [],
    rowLimit: 100,
    parallel: { on: false, degree: 4 },
    analytics: [],
    pivot: defaultPivot(),
  };
}

/** 불러온 정의 — 빠진 칸만 기본값으로 채우고 나머지(모르는 칸 포함)는 그대로 */
export function withDefaults(spec) {
  const d = defaultSpec();
  const s = spec && typeof spec === 'object' ? spec : {};
  return {
    ...d,
    ...s,
    source: { ...d.source, ...(s.source || {}) },
    templateParams: { ...d.templateParams, ...(s.templateParams || {}) },
    parallel: { ...d.parallel, ...(s.parallel || {}) },
    pivot: { ...d.pivot, ...(s.pivot || {}) },
    columns: Array.isArray(s.columns) ? s.columns : [],
    analytics: Array.isArray(s.analytics) ? s.analytics : [],
    // 더 새 판의 번호는 그대로 둔다(끌어내리지 않는다)
    version: compareVersion(s.version, SPEC_VERSION) > 0 ? s.version : SPEC_VERSION,
  };
}

export function makeColumnEntry(meta, sampleRow) {
  const sampleVal = sampleRow ? sampleRow[meta.sourceName != null ? meta.sourceName : meta.name] : null;
  const dataType = inferDataType(meta, sampleVal);
  const defaultMode = dataType === 'number' ? 'eq' : dataType === 'date' ? 'date_eq' : 'both%';
  return {
    name: meta.name,
    comment: meta.comment || '',
    type: meta.type || '',
    dataType,
    group: false,
    filter: false,
    sort: false,
    hide: false,
    filterValue: '',
    filterValue2: '',
    filterMode: defaultMode,
    aggFn: 'NONE',
    alias: '',
    sortDir: 'ASC',
    sortPriority: 1,
  };
}

/**
 * 대상의 컬럼으로 설정을 맞춘다 — 이름이 같은 컬럼의 설정은 남기고(주석·형식은 새것으로), 없어진 컬럼은 빼고, 새 컬럼은 기본값으로.
 * 같은 이름 컬럼(조인 결과)은 생성기와 **같은 규칙**으로 이름을 붙인다(uniqueColumnNames).
 */
export function syncColumns(prev, metas, sampleRow) {
  const list = (metas || []).filter(m => m && m.name);
  if (!list.length) return prev || [];
  const names = uniqueColumnNames(list.map(m => m.name));
  const items = list.map((m, i) => ({ ...m, name: names[i], sourceName: m.name }));
  const byName = new Map((prev || []).map(c => [c.name, c]));
  return items.map((meta) => {
    const old = byName.get(meta.name);
    return old ? { ...old, comment: meta.comment || old.comment || '', type: meta.type || old.type || '' } : makeColumnEntry(meta, sampleRow);
  });
}
